import React, { useState } from "react";
import {
  Grid,
  IconButton,
  InputAdornment,
  Typography,
  Box,
  CircularProgress
} from "@mui/material";
import { makeValidate, makeRequired, showErrorOnBlur } from "mui-rff";
import { Form } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Alert } from "@mui/material";
import {
  CheckBoxWrapper,
  PageAlert,
  PageBox,
  FormButton,
  FormErrorHelperText,
  FormTextField,
  PageHeader,
  PageLink,
  TwoFaFormGroup,
  TwoFaCodeField
} from "./Login.style";
//We are using our own version of the Checkbox instead of mui-rff's version because we need to hook into the checked and onChange props.
//This can removed this if this issue is ever addressed and fixed.
import { Checkboxes } from "../../components/Mui/Checkbox";
import { AlertStateInterface } from "../../interfaces/AlertStateInterface";
import { LoginSchema } from "../../schema/LoginSchema";
import { LoginInterface } from "../../interfaces/LoginInterface";
import { useAuth, ResponseStatus } from "../../providers/Auth";

//import NewPassword from "../../components/NewPassword/NewPassword";
import { TabTitle } from "../../utils/GeneralFunctions";
import { TwoFaInterface } from "../../interfaces/TwoFaInterface";

/**
 * Uses the optional helper makeValidate function to format the error messages
 * into something usable by final form.
 */
const validate = makeValidate(LoginSchema, error => {
  return <span className="error">{i18n.t(error.message)}</span>;
});
/**
 * Grabs all the required fields from the schema so that they can be passed into
 * the components without having to declare them in both the schema and the component.
 */
const required = makeRequired(LoginSchema);

const Login = () => {
  const auth = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<AlertStateInterface | null>(location.state || null);
  TabTitle(t("main.header.page.title"));

  const [submittedValues, setSubmittedValues] = useState<LoginInterface | undefined>(undefined);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberEmail, setRememberEmail] = useState<boolean>(
    localStorage.getItem("rememberEmail") === "true" || false
  );
  const [email, setEmail] = useState(localStorage.getItem("email") || "");
  const [password, setPassword] = useState("");

  const [resending, setResending] = useState(false);
  const [sendingForm, setSendingForm] = useState(false);

  const [twoFaState, setTwoFaState] = useState<TwoFaInterface>({
    twoFaStepEnabled: false,
    submitEnabled: true,
    firstLanding: false,
    limitReached: false,
    invalidAuthCode: false,
    codeResendSuccess: false,
    twoFACode: "",
    username: "",
    token: "", // Optional field for token, if needed
    passl: 0, // Represents the pass limit, can be used to track attempts or
    authCodeExpired: false // Optional field to indicate if the auth code has expired
  });

  const initialValues: LoginInterface = {
    email: email,
    password: (twoFaState?.passl) ? "*".repeat(twoFaState.passl) : password,
    rememberEmail: rememberEmail
  };

  const onChangeRememberMe = e => {
    let value = e.target.checked;
    setRememberEmail(value);
  };

  const onChangeEmail = value => {
    setEmail(value);
  };

  const onChangePassword = value => {
    setPassword(value);
  };

  const onSubmit = async (values: LoginInterface) => {

    setSubmittedValues(values);

    //handle rememberEmail
    if (values.email !== "" && values.rememberEmail === true) {
      localStorage.setItem("rememberEmail", values.rememberEmail.toString());
      localStorage.setItem("email", values.email);
    } else {
      localStorage.setItem("rememberEmail", "false");
      localStorage.setItem("email", "");
    }

    // lowercase the email and trim white space around password
    const signInResult: any = await auth.signIn(values.email.toLowerCase(),values.password.trim());

    switch (signInResult?.status) {
      case ResponseStatus.BAD_CREDENTIALS:
        setState({
          showAlert: true,
          severity: "error",
          message: signInResult.message
        });
        setTwoFaState({
          twoFaStepEnabled: false,
          submitEnabled: true,
          firstLanding: false,
          limitReached: false,
          invalidAuthCode: false,
          codeResendSuccess: false,
          twoFACode: "",
          username: values.email,
          token: "",
          passl: values.password.length,
          authCodeExpired: false
        });
      break;

      case ResponseStatus.TWO_FA:
        setState({
          showAlert: false,
          severity: "error",
          message: ""
        });
        setTwoFaState({
          twoFaStepEnabled: true,
          submitEnabled: false,
          firstLanding: true,
          limitReached: false,
          invalidAuthCode: false,
          codeResendSuccess: false,
          twoFACode: "",
          username: values.email,
          token: signInResult.token, // Store the token for 2FA verification
          passl: values.password.length,
          authCodeExpired: false
        });
      break;

      case ResponseStatus.TWO_FA_LIMIT_REACHED:
        setState({
          showAlert: false,
          severity: "error",
          message: ""
        });
        setTwoFaState({
          twoFaStepEnabled: true,
          submitEnabled: true,
          firstLanding: false,
          limitReached: true,
          twoFaError: {
            severity: "error",
            message: t("login.block-form.error.3002")
          },
          invalidAuthCode: false,
          codeResendSuccess: false,
          username: values.email,
          passl: values.password.length,
          authCodeExpired: false
        });
      break;

      case ResponseStatus.TWO_FA_CODE_EXPIRED:
        setTwoFaState({
          twoFaStepEnabled: false,
          submitEnabled: true,
          firstLanding: false,
          limitReached: false,
          invalidAuthCode: false,
          codeResendSuccess: false,
          twoFACode: "",
          username: values.email,
          token: signInResult.token, // Store the token for 2FA verification
          passl: values.password.length,
          authCodeExpired: true
        });
      break;

      case ResponseStatus.RESET_PASSWORD:
        navigate("/reset-password", {
          state: {
            showAlert: true,
            severity: "error",
            message: signInResult.message
          }
        });
      break;

      case ResponseStatus.FAIL || signInResult?.error:
        setState({
          showAlert: true,
          severity: "error",
          message: signInResult.message || t("all.block-form.error.0000")
        });
      break;

      case ResponseStatus.SUCCESS:
        const userProfileResult: any = await auth.getUserProfile();
        if (userProfileResult.status === ResponseStatus.SUCCESS) {
          navigate("/home");
        } else {
          navigate("/login", {
            state: {
              showAlert: true,
              severity: "error",
              message: t("all.block-form.error.0000")
            }
          });
        }
      break;
    }
  };

  // Handler for 2FA code submission
  const twoFaSubmit = async (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, form: any) => {
    setSendingForm(true);

    if (
      twoFaState?.token
      && twoFaState.token.length > 0
    ) {
      auth.tokenLogin(twoFaState.token);
    }

    const clearAuthCode = () => {
      Array.from({ length: 6 }).forEach((_, idx) => {
          form.change(`twoFACode${idx}`, "");
          const inputs = document.getElementsByName(`twoFACode${idx}`);
          inputs.forEach(input => {
            if (input instanceof HTMLInputElement) {
              input.value = "";
            }
          });
      });
    };

    const code = Array.from({ length: 6 })
      .map((_, idx) => {
        const input = document.querySelector<HTMLInputElement>(`input[name="twoFACode${idx}"]`);
        return input ? input.value : "";
      })
      .join("");

    if (code.length < 1) {
      setSendingForm(false);
      setTwoFaState({
        ...twoFaState,
        twoFaStepEnabled: true,
        submitEnabled: true,
        authCodeExpired: false,
        twoFACode: code,
        twoFaError: {
          severity: "error",
          message: t("login.2fa.code-required")
        },
        invalidAuthCode: false,
        firstLanding: true,
        limitReached: false,
        codeResendSuccess: false,
        username: initialValues.email,
        passl: initialValues.password.length
      });
      return;
    }

    const verifyResult: any = await auth.verify2FA(code);

    switch (verifyResult?.status) {

        case ResponseStatus.SUCCESS:
          const userProfileResult: any = await auth.getUserProfile();
          if (userProfileResult.status === ResponseStatus.SUCCESS) {
            navigate("/home");
          } else {
            navigate("/login", {
              state: {
                showAlert: true,
                severity: "error",
                message: t("all.block-form.error.0000")
              }
            });
          }
        break;

        case ResponseStatus.TWO_FA_ERROR:
          setTwoFaState({
            ...twoFaState,
            twoFaStepEnabled: true,
            submitEnabled: true,
            firstLanding: false,
            limitReached: false,
            invalidAuthCode: true,
            codeResendSuccess: false,
            twoFACode: "",
            username: initialValues.email,
            passl: initialValues.password.length
          });
          setSendingForm(false);
          clearAuthCode();
        break;

        case ResponseStatus.TWO_FA_CODE_EXPIRED:
          setTwoFaState({
            ...twoFaState,
            twoFaStepEnabled: true,
            submitEnabled: true,
            firstLanding: false,
            limitReached: false,
            invalidAuthCode: false,
            codeResendSuccess: false,
            twoFACode: code,
            username: initialValues.email,
            token: verifyResult.token, // Store the token for 2FA verification
            passl: initialValues.password.length,
            authCodeExpired: true
          });
          setSendingForm(false);
          clearAuthCode();
        break;

        case ResponseStatus.FAIL:
          setState({
            showAlert: false,
            severity: "",
            message: ""
          });
          setTwoFaState({
            ...twoFaState,
            twoFaStepEnabled: true,
            submitEnabled: true,
            firstLanding: false,
            limitReached: false,
            invalidAuthCode: true,
            codeResendSuccess: false,
            twoFACode: code,
            username: initialValues.email,
            passl: initialValues.password.length
          });
          setSendingForm(false);
          clearAuthCode();
          return;

        default:
          setTwoFaState({
            ...twoFaState,
            twoFaStepEnabled: true,
            submitEnabled: true,
            firstLanding: false,
            limitReached: false,
            invalidAuthCode: true,
            codeResendSuccess: false,
            twoFACode: code,
            username: initialValues.email,
            passl: initialValues.password.length
          });
          setSendingForm(false);
        break;
      }

  };

  const resend2FACode = async (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, form: any) => {
    e.preventDefault();

    // Reset all twoFACode fields
    // setTwoFACode("");
    setTwoFaState({
      ...twoFaState,
      twoFACode: ""
    });
    Array.from({ length: 6 }).forEach((_, idx) => {
      form.change(`twoFACode${idx}`, "");
      const inputs = document.getElementsByName(`twoFACode${idx}`);
      inputs.forEach(input => {
        if (input instanceof HTMLInputElement) {
          input.value = "";
        }
      });
    });
    setResending(true);
    const resendResult: any = await auth.resend2FA();

    setResending(false);
    switch (resendResult?.status) {
      case ResponseStatus.SUCCESS:
        setState({
          showAlert: false,
          severity: "",
          message: ""
        });
        setTwoFaState({
          ...twoFaState,
          twoFaStepEnabled: true,
          submitEnabled: true,
          firstLanding: false,
          limitReached: false,
          invalidAuthCode: false,
          codeResendSuccess: true,
          twoFACode: "",
          username: twoFaState.username,
          token: twoFaState.token, // Keep the token for 2FA verification
          passl: twoFaState.passl,
          authCodeExpired: false
        });
      return;

      case (ResponseStatus.TWO_FA_LIMIT_REACHED):
        setTwoFaState({
          ...twoFaState,
          twoFaStepEnabled: true,
          submitEnabled: true,
          firstLanding: false,
          limitReached: true,
          invalidAuthCode: false,
          codeResendSuccess: false,
          twoFACode: "",
          authCodeExpired: false
        });
      return;

      case (resendResult?.status === ResponseStatus.FAIL || resendResult.error):
        setTwoFaState({
          ...twoFaState,
          twoFaStepEnabled: true,
          submitEnabled: true,
          firstLanding: false,
          limitReached: false,
          invalidAuthCode: true,
          codeResendSuccess: false,
          twoFACode: "",
          username: initialValues.email,
          passl: initialValues?.password?.length || 0,
          authCodeExpired: false
        });
      return;
    }

    setTwoFaState({
      ...twoFaState,
      twoFaStepEnabled: true,
      submitEnabled: true,
      firstLanding: false,
      invalidAuthCode: false,
      codeResendSuccess: false
    });

  };

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  return (
     <>
     {state && state.showAlert && (
        <PageAlert
          severity={
            state.severity === "success"
              ? "success"
              : state.severity === "error"
              ? "error"
              : state.severity === "info"
              ? "info"
              : "warning"
          }
        >
          {t(state.message)}
        </PageAlert>
     )}
     {twoFaState?.twoFaError?.message && (
        <PageAlert
          severity={
            twoFaState.twoFaError.severity
          }
        >
          {twoFaState.twoFaError.message}
        </PageAlert>
     )}
    <PageBox>
      <Grid container sx={{ pt: 0 }}>
        <Grid item xs={12} sm={6}>
        <PageHeader>{t("main.header.page.title")}</PageHeader>
        <Typography component="p" variant="lead">
          {t("main.header.page.description")}
        </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Form
            onSubmit={onSubmit}
            initialValues={
              submittedValues ? submittedValues : initialValues
            }
            validate={validate}
            render={({ submitError, handleSubmit, submitting, form }) => (
              <form
                onSubmit={handleSubmit}
                //noValidate={true}
                autoComplete="new-password"
              >
                <FormTextField
                  type="text"
                  required={required.email}
                  label={t("login.block-form.input.username")}
                  id="email"
                  name="email"
                  variant="filled"
                  autoComplete="email"
                  InputProps={{ disableUnderline: true }}
                  showError={showErrorOnBlur}
                  value={twoFaState?.username || email}
                  disabled={twoFaState.twoFaStepEnabled}
                />
                <OnChange name="email">
                  {value => {
                    onChangeEmail(value);
                  }}
                </OnChange>
                <FormTextField
                  disabled={twoFaState.twoFaStepEnabled}
                  required={required.password}
                  type={showPassword ? "text" : "password"}
                  label={t("login.block-form.input.password")}
                  id="password"
                  name="password"
                  variant="filled"
                  autoComplete="current-password"
                  showError={showErrorOnBlur}
                  InputProps={{
                    disableUnderline: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showPassword
                              ? t("all.button.password-icon-show")
                              : t("all.button.password-icon-hide")
                          }
                          onClick={() => setShowPassword(!showPassword)}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          component="button"
                          disableRipple
                        >
                          {showPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <OnChange name="password">
                  {value => {
                    onChangePassword(value);
                  }}
                </OnChange>
                <CheckBoxWrapper
                  sx={{
                    display: "block"
                  }}
                >
                  <Checkboxes
                    required={required.rememberEmail}
                    id="rememberEmail"
                    name="rememberEmail"
                    data={{
                      label: t("login.block-form.input.checkbox"),
                      value: true,
                      onChange: onChangeRememberMe,
                      checked: rememberEmail
                    }}
                    disableRipple
                    disabled={(twoFaState.twoFaStepEnabled)}
                  />
                </CheckBoxWrapper>
                {submitError && (
                  <FormErrorHelperText
                    variant="filled"
                    filled
                    required
                    component="p"
                  >
                    <span className="error">{t(submitError)}</span>
                  </FormErrorHelperText>
                )}
                <FormButton
                  variant="contained"
                  type="submit"
                  disabled={twoFaState.twoFaStepEnabled || submitting ? true : false}
                  size="large"
                  sx={{ mr: theme => theme.spacer_m }}
                  disableElevation
                >
                  {t("login.block-form.button.submit")}
                </FormButton>
                <PageLink
                  component={Link}
                  to="/forgot-password"
                  variant="body1"
                  color="fuchsiaAccessible.main"
                >
                  {t("login.block-form.button.link")}
                </PageLink>

                { twoFaState.twoFaStepEnabled && (
                  <Box sx={{ mb: "3rem",padding: "1rem", borderRadius: "10px", display: "flex", flexDirection: "column", alignItems: "center", gap: "2rem" }}>
                    <h3 style={{ textAlign: "center", marginTop: "5rem", marginBottom: 0}}>{t("login.2fa.page-title")}</h3>
                    <p style={ { textAlign: "center"} }>
                      { (twoFaState?.firstLanding) && (
                        <Alert severity="info">{t("login.2fa.info")}</Alert>
                      )}
                      { (twoFaState?.invalidAuthCode) && (
                          <Alert severity="error">{t("login.2fa.code-invalid")}</Alert>
                      )}
                      { (twoFaState?.authCodeExpired) && (
                        <Alert severity="error">{t("login.block-form.error.3003")}</Alert>
                      )}
                      { (twoFaState?.codeResendSuccess) && (
                        <Alert severity="success">
                          {t("login.2fa.resend.success")}
                        </Alert>
                      )}
                      { (twoFaState?.limitReached) && (
                        <Alert severity="error">
                          {t("login.block-form.error.3002")}
                        </Alert>
                      )}
                    </p>
                    <TwoFaFormGroup row sx={{ justifyContent: "center", mb: 2, gap: "1rem" }}>
                        {[...Array(6)].map((_, idx) => (
                          <TwoFaCodeField
                            key={idx}
                            name={`twoFACode${idx}`}
                            type="text"
                            inputProps={{
                              inputMode: "numeric",
                              pattern: "[0-9]",
                              maxLength: 1,
                              style: { textAlign: "center", fontSize: "2rem", width: "4rem", height: "4rem" },
                              autoFocus: idx === 0,
                              tabIndex: idx + 1,
                              'data-idx': idx,
                              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                                const target = e.target as HTMLInputElement;
                                if (!/[0-9]/.test(e.key) && e.key !== "Backspace" && e.key !== "Tab" && e.key !== "ArrowLeft" && e.key !== "ArrowRight") {
                                  e.preventDefault();
                                }
                                // Move to previous field on Backspace if empty
                                if (e.key === "Backspace" && !target.value && idx > 0) {
                                  const prev = document.querySelector<HTMLInputElement>(`input[data-idx="${idx - 1}"]`);
                                  prev?.focus();
                                }
                              },
                              onInput: (e: React.FormEvent<HTMLInputElement>) => {
                                const target = e.target as HTMLInputElement;
                                target.value = target.value.replace(/[^0-9]/g, "");
                              },
                              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                const evalue = e.target.value;
                                const val = evalue.replace(/[^0-9]/g, "");
                                // Move to next field if value entered
                                if (val && idx < 5) {
                                  const next = document.querySelector<HTMLInputElement>(`input[data-idx="${idx + 1}"]`);
                                  next?.focus();
                                }

                                // Check if all 6 fields are filled
                                const allFilled = Array.from({ length: 6 }).every((_, i) => {
                                  const input = document.querySelector<HTMLInputElement>(`input[data-idx="${i}"]`);
                                  return input && input.value.length === 1;
                                });
                                setTwoFaState({
                                  ...twoFaState,
                                  submitEnabled: allFilled,
                                });
                              }
                            }}
                            value= { twoFaState?.twoFACode ? (twoFaState.twoFACode[idx]?.length > 0 && twoFaState.twoFACode[idx] !== "") ? twoFaState.twoFACode[idx] : null : null}
                            variant="filled"
                            required
                            sx={{ mx: 0.5 }}
                          />
                        ))}
                      </TwoFaFormGroup>
                      <Box sx={{ mb: 2 }} style={{ textAlign: "center" }}>
                        <PageLink
                            component={Link}
                            to="#"
                            onClick={e => !resending && resend2FACode(e, form)}
                            variant="body1"
                            color="fuchsiaAccessible.main"
                            style={{ pointerEvents: resending ? "none" : "auto", opacity: resending ? 0.6 : 1 }}
                          >
                            {resending ? t("login.2fa.resend.loading") : t("login.2fa.resend.link")}
                        </PageLink>
                        <br />
                      </Box>
                      <FormButton
                        variant="contained"
                        type="button"
                        onClick={e => twoFaSubmit(e, form)}
                        size="large"
                        disabled={submitting || !twoFaState.submitEnabled}
                        sx={{
                          display: "block",
                          margin: "0 auto",
                          minWidth: "25rem"
                        }}
                        disableElevation
                      >
                        { sendingForm ? (
                          <CircularProgress color="inherit" size="2rem" />
                        ) : (
                          t("login.2fa.button.submit")
                        )}
                      </FormButton>
                  </Box>
                )}
              </form>
            )}
          />
        </Grid>
      </Grid>
    </PageBox>
   </>
  );
};

export default Login;
